import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import config from 'config/runtimeConfig';
import { deepTrimStrings } from 'utils/trimWhitespace';
import { SESSION_ENDING_ERROR_CODES } from './errorCodes';

export const URL = config.APP_URL;
export const API_URL = config.API_URL;
export const JITSI_VOICE_SERVER_URL = config.JITSI_VOICE_SERVER_URL;
export const JITSI_VIDEO_SERVER_URL = config.JITSI_VIDEO_SERVER_URL;

// --- Store injection (evita dependencia circular store ↔ api) ---
// Llamar a injectStore(store) en index.js después de crear el store.

interface MinimalStore {
  getState: () => {
    common: {
      user: {
        userLogged: { token: string } | null;
        sacInfo: { sessionToken: string; providerId: string; providerName: string } | null;
      };
    };
  };
  dispatch: (action: { type: string; payload?: unknown }) => unknown;
}

let _store: MinimalStore | undefined;

export const injectStore = (store: MinimalStore): void => {
  _store = store;
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
});

const apiSac = axios.create({
  baseURL: API_URL,
  timeout: 15000,
});

const apiVidal = axios.create({
  baseURL: 'https://wsdata.vademecum.es/vweb/json/webservices/getTableData_new',
  timeout: 15000,
});

// --- Interceptores de request: inyección automática de credenciales ---
// `api`     → inyecta `token` desde state.common.user.userLogged.
// `apiSac`  → inyecta `sessionToken/providerId/providerName` desde state.common.user.sacInfo.
// Las funciones del servicio ya no aceptan estas credenciales como argumento;
// el interceptor es la única fuente de verdad.

api.interceptors.request.use((reqConfig) => {
  const token = _store?.getState().common.user.userLogged?.token;
  if (token) {
    reqConfig.headers = reqConfig.headers ?? {};
    reqConfig.headers.token = token;
  }
  return reqConfig;
});

apiSac.interceptors.request.use((reqConfig) => {
  const sacInfo = _store?.getState().common.user.sacInfo;
  if (sacInfo) {
    reqConfig.headers = reqConfig.headers ?? {};
    reqConfig.headers.sessionToken = sacInfo.sessionToken;
    reqConfig.headers.providerId = sacInfo.providerId;
    reqConfig.headers.providerName = sacInfo.providerName;
  }
  return reqConfig;
});

// --- Interceptor de request: recorte de espacios en los extremos ---
// Todo texto que sale del front (body y query params) se recorta: al copiar y pegar
// se cuelan tabuladores, espacios duros o caracteres de ancho cero que acababan en BD.
// Es la red de seguridad de todos los formularios, presentes y futuros. Las claves
// de contraseña no se tocan (ver `utils/trimWhitespace`).

const trimRequestStrings = (reqConfig: AxiosRequestConfig): AxiosRequestConfig => {
  reqConfig.data = deepTrimStrings(reqConfig.data);
  reqConfig.params = deepTrimStrings(reqConfig.params);
  return reqConfig;
};

[api, apiSac, apiVidal].forEach((instance) => instance.interceptors.request.use(trimRequestStrings));

// --- Interceptores de respuesta ---
// Centralizan el log de errores y garantizan que los errores se propaguen
// correctamente a los thunks de Redux (estado 'failed').
// E3: detecta errorCode 1000 (sesión expirada) y lo gestiona aquí en lugar
// de dispersar el check en cada thunk. Desde el bloqueo de acceso (V50) también
// entra por aquí el 1009: el back lo devuelve en cualquier petición de un usuario
// al que han bloqueado mientras trabajaba, y es lo que lo saca de la aplicación.

const onResponseError = (prefix: string) => (error: AxiosError): Promise<never> => {
  const status = error.response?.status ?? 'Network Error';
  const url = error.config?.url ?? '';
  const data = error.response?.data as Record<string, unknown> | undefined;
  const msg = (data?.message as string) ?? error.message;
  console.error(`[${prefix}] ${status} ${url} — ${msg}`);
  return Promise.reject(error);
};

api.interceptors.response.use((response) => {
  const errorCode = (response.data as Record<string, unknown>)?.errorCode as number | undefined;
  if (errorCode !== undefined && SESSION_ENDING_ERROR_CODES.includes(errorCode)) {
    _store?.dispatch({ type: 'common/setSessionExpired', payload: errorCode });
  }
  return response;
}, onResponseError('API'));
apiSac.interceptors.response.use((r) => r, onResponseError('SAC'));
apiVidal.interceptors.response.use((r) => r, onResponseError('Vidal'));

// --- Funciones de petición ---

type Body = object | null;
type QueryParams = object | null;

/** `headers`: cabeceras extra, p. ej. la `Review-Token` de la página pública de revisión de facturación. */
export const get = (
  service: string,
  url: string,
  data: Body = {},
  params: QueryParams = {},
  headers: Record<string, string> = {},
): Promise<AxiosResponse> => {
  return api.get(service.concat(url), {
    data,
    params,
    paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat' }),
    headers: { Accept: 'application/json', ...headers },
  });
};

export const post = (
  service: string,
  url: string,
  data: Body = {},
  params: QueryParams = {},
  headers: Record<string, string> = {},
): Promise<AxiosResponse> => {
  return api.post(service.concat(url), data, {
    params,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...headers },
  });
};

export const put = (
  service: string,
  url: string,
  data: Body = {},
  params: QueryParams = {},
): Promise<AxiosResponse> => {
  return api.put(service.concat(url), data, {
    params,
    headers: { Accept: 'application/json' },
  });
};

export const del = (service: string, url: string): Promise<AxiosResponse> => {
  return api.delete(service.concat(url), {
    headers: { Accept: 'application/json' },
  });
};

/**
 * Descarga un fichero. Si el back responde con un error (que llega como JSON, no como fichero), la promesa se rechaza
 * con ese JSON en `error.response.data` ya parseado, para que el llamante pueda leer su `errorCode`.
 */
export const getBlob = async (
  service: string,
  url: string,
  headers: Record<string, string> = {},
): Promise<AxiosResponse<Blob>> => {
  try {
    return await api.get(service.concat(url), { responseType: 'blob', timeout: 60000, headers });
  } catch (error) {
    const blob = (error as AxiosError)?.response?.data;
    if (blob instanceof Blob) {
      try {
        (error as AxiosError).response!.data = JSON.parse(await blob.text());
      } catch {
        // no era JSON: se deja tal cual
      }
    }
    throw error;
  }
};

/** POST multipart: con FormData axios pone él solo el Content-Type con su boundary, así que no se fija. */
export const postForm = (service: string, url: string, form: FormData): Promise<AxiosResponse> => {
  return api.post(service.concat(url), form, {
    headers: { Accept: 'application/json' },
  });
};

// SAC credentials (sessionToken/providerId/providerName) are injected by the
// apiSac request interceptor from state.common.user.sacInfo — the helpers no
// longer need them as arguments.

export const getAlarm = (
  service: string,
  url: string,
  data: Body = {},
  params: QueryParams = {},
): Promise<AxiosResponse> => {
  return apiSac.get(service.concat(url), {
    data,
    params,
    headers: { Accept: 'application/json' },
  });
};

export const postAlarm = (
  service: string,
  url: string,
  data: Body = {},
  params: QueryParams = {},
): Promise<AxiosResponse> => {
  return apiSac.post(service.concat(url), data, {
    params,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  });
};

export const putAlarm = (
  service: string,
  url: string,
  data: Body = {},
  params: QueryParams = {},
): Promise<AxiosResponse> => {
  return apiSac.put(service.concat(url), data, {
    params,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  });
};

export const getVidal = (data: Body = {}, params: QueryParams = {}): Promise<AxiosResponse> => {
  return apiVidal.get('', {
    data,
    params,
    headers: { Accept: 'application/json' },
  });
};

export const getIcd = (
  service: string,
  url: string,
  data: Body = {},
  params: QueryParams = {},
  language: string,
): Promise<AxiosResponse> => {
  return api.get(service.concat(url), {
    data,
    params,
    headers: { Accept: 'application/json', 'API-Version': 'v2', 'Accept-Language': language },
  });
};
